import { useId, useMemo, useState, type ReactNode } from 'react';
import { Modal } from 'antd';
import { I } from '../yeni-design/icons';
import { RaporTablosu } from '../rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../rapor-ekrani/RaporHatasi';
import { useRaporSorgu } from '../rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../rapor-ekrani/useDarEkran';
import { paraMetni, tarihMetni } from '../rapor-ekrani/bicim';
import type { RaporKolonu } from '../rapor-ekrani/kolon';
import type { PanelEkranKaynagi } from '../../types/panelConfig';
import { belgeDetayi, type BelgeDetayi, type BelgeKalemi, type HamBelgeSatiri } from './belgeDetayi';
import type { HareketSatiri } from './tipler';

interface Props {
  /** Açık hareket; `null` ise modal kapalıdır. */
  hareket: HareketSatiri | null;
  /** `belge` ds'i (`@FIS`); firmada tanımlı değilse undefined. */
  kaynak: PanelEkranKaynagi | undefined;
  onKapat: () => void;
}

/**
 * S359: cari hareketine basınca açılan belge detayı (Yerlice `BelgeDetayModal` düzeni): başlıkta belge no
 * ve tür rozeti; solda Tarihler / Cari / Belge grupları; sağda ürün satırları ve altında ERP sırasıyla
 * toplamlar. Telefonda (<= 767) tam ekran, gruplar "Belge bilgileri" düğmesinin arkasında kapalı, Genel
 * Toplam altta sabit şeritte (toplam bloğundaki aynı satır gizlenir, sayı ekranda tek yerde).
 *
 * Fişi olmayan hareket (tahsilat, ödeme, virman) de açılır (kullanıcı kararı 2026-10-15): satırdaki bilgi
 * ve "Bu hareket türünde ürün satırı yok". Sorgu yalnız fişli harekette atılır.
 */
export function BelgeDetayModali({ hareket, kaynak, onKapat }: Props) {
  const telefon = useDarEkran();
  const fisli = !!(hareket?.fis && kaynak);
  return (
    <Modal
      open={hareket !== null}
      onCancel={onKapat}
      footer={null}
      closable={false}
      destroyOnHidden
      centered={!telefon}
      // Fişsiz harekette tablo yok: dar pencere (1100 px'te bilgi kutusu boşlukta kalıyordu).
      width={telefon ? '100vw' : fisli ? 'min(1100px, 94vw)' : 600}
      style={telefon ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : undefined}
      styles={{
        content: telefon
          ? { height: '100dvh', borderRadius: 0, padding: 0, display: 'flex', flexDirection: 'column' }
          : { padding: 0, maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
        body: { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' },
      }}
      title={null}
    >
      {hareket && (
        fisli && hareket.fis && kaynak
          ? <FisliBelge key={hareket.fis} hareket={hareket} fis={hareket.fis} kaynak={kaynak} onKapat={onKapat} />
          : <Cerceve
              baslik={hareket.belgeNo || hareket.islem || 'Hareket'}
              rozet={hareket.islem}
              onKapat={onKapat}
              govde={<HareketBilgisi hareket={hareket} not={hareket.fis ? 'Bu firmada belge ayrıntısı henüz tanımlı değil.' : 'Bu hareket türünde ürün satırı yok.'} />}
            />
      )}
    </Modal>
  );
}

/** Başlık şeridi + gövde + (telefonda) altta sabit Genel Toplam. */
function Cerceve({ baslik, rozet, ek, onKapat, govde, genelToplam }: {
  baslik: string;
  rozet?: string;
  ek?: ReactNode;
  onKapat: () => void;
  govde: ReactNode;
  genelToplam?: number;
}) {
  const telefon = useDarEkran();
  return (
    <div className="flex flex-col flex-1 min-h-0 font-sans" data-testid="belge-detay">
      <div
        className="flex items-center gap-2 border-b border-border bg-surface"
        style={{ padding: telefon ? 'calc(6px + env(safe-area-inset-top, 0px)) 8px 6px 16px' : '12px 16px' }}
      >
        <h2 className="m-0 text-[16px] font-semibold leading-snug text-ink-900 truncate min-w-0" data-testid="belge-detay-baslik">
          {baslik}
        </h2>
        {rozet && (
          <span className="shrink-0 rounded-full border border-border bg-ink-50 px-2 py-0.5 text-[12px] text-ink-700" data-testid="belge-detay-rozet">
            {rozet}
          </span>
        )}
        {ek}
        <button
          type="button"
          onClick={onKapat}
          aria-label="Kapat"
          className={`ml-auto shrink-0 focus-ring inline-flex items-center justify-center rounded-md border border-border-strong bg-surface text-ink-700 hover:bg-ink-50 ${telefon ? 'w-11 h-11' : 'w-8 h-8'}`}
        >
          <I.X size={telefon ? 18 : 16} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto bg-[#f8fafc]" data-testid="belge-detay-govde">{govde}</div>
      {telefon && genelToplam !== undefined && (
        <div
          className="flex items-center justify-between border-t border-border bg-surface px-4 py-3 text-[14px]"
          style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
          data-testid="belge-detay-genel-serit"
        >
          <span className="text-ink-700">Genel Toplam</span>
          <b className="mono text-ink-900">{paraMetni(genelToplam)}</b>
        </div>
      )}
    </div>
  );
}

/** Etiketli alan grubu (Yerlice "Tarihler", "Cari", "Belge"). Boş alan tire ile doldurulmaz, hiç basılmaz. */
function Grup({ baslik, alanlar }: { baslik: string; alanlar: Array<[string, string, boolean?]> }) {
  const dolu = alanlar.filter(([, v]) => v !== '');
  if (dolu.length === 0) return null;
  return (
    <section className="bg-surface border border-border rounded-lg px-3 py-2.5">
      <h3 className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-500">{baslik}</h3>
      <dl className="m-0 grid gap-1.5 text-[13px]">
        {dolu.map(([etiket, deger, tam]) => (
          <div key={etiket} className={tam ? 'grid gap-0.5' : 'flex justify-between gap-3'}>
            <dt className="text-ink-500 shrink-0">{etiket}</dt>
            <dd className={`m-0 text-ink-900 ${tam ? 'break-words' : 'text-right'}`}>{deger}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Fişsiz hareket ya da bulunamayan / tanımsız belge: satırdaki bilgi + not. */
function HareketBilgisi({ hareket, not }: { hareket: HareketSatiri; not: string }) {
  const tutar = hareket.borc ? ['Borç', paraMetni(hareket.borc)] : ['Alacak', paraMetni(hareket.alacak)];
  return (
    <div className="flex flex-col gap-3 p-4" data-testid="belge-detay-hareket">
      <Grup baslik="Hareket" alanlar={[
        ['Tarih', tarihMetni(hareket.tarih)],
        ['İşlem', hareket.islem],
        ['Belge No', hareket.belgeNo],
        ['Vade', tarihMetni(hareket.vade)],
        [tutar[0], tutar[1]],
        ['Açıklama', hareket.aciklama, true],
      ]} />
      <p className="m-0 text-[13px] text-ink-500" data-testid="belge-detay-satir-yok">{not}</p>
    </div>
  );
}

function FisliBelge({ hareket, fis, kaynak, onKapat }: { hareket: HareketSatiri; fis: string; kaynak: PanelEkranKaynagi; onKapat: () => void }) {
  // Kimlik METİN gider (Hamam adisyonu gibi): sayıya çevrilmez.
  const q = useRaporSorgu<HamBelgeSatiri>(kaynak, { '@FIS': fis });
  const belge = useMemo(() => belgeDetayi(q.data ?? []), [q.data]);
  const baslik = belge?.belgeNo || hareket.belgeNo || hareket.islem || 'Belge';
  const rozet = belge?.tur || hareket.islem;

  if (q.isError) {
    return (
      <Cerceve baslik={baslik} rozet={rozet} onKapat={onKapat} govde={
        <div className="p-4"><RaporHatasi testid="belge-detay-hata" mesaj="Belge yüklenemedi." onTekrar={() => q.refetch()} /></div>
      } />
    );
  }
  if (q.isLoading) {
    return (
      <Cerceve baslik={baslik} rozet={rozet} onKapat={onKapat} govde={
        <p className="m-0 p-4 text-[13px] text-ink-500" data-testid="belge-detay-yukleniyor">Belge yükleniyor…</p>
      } />
    );
  }
  if (!belge) {
    return (
      <Cerceve baslik={baslik} rozet={rozet} onKapat={onKapat}
        govde={<HareketBilgisi hareket={hareket} not="Bu hareketin belgesi bulunamadı." />} />
    );
  }
  return (
    <Cerceve
      baslik={baslik}
      rozet={rozet}
      ek={belge.doviz && (
        <span className="shrink-0 rounded-full border border-border bg-ink-50 px-2 py-0.5 text-[12px] text-ink-700" data-testid="belge-detay-doviz">
          Tutarlar {belge.doviz}
        </span>
      )}
      onKapat={onKapat}
      genelToplam={belge.genelToplam}
      govde={<BelgeGovdesi belge={belge} hareket={hareket} />}
    />
  );
}

function BelgeGovdesi({ belge, hareket }: { belge: BelgeDetayi; hareket: HareketSatiri }) {
  const telefon = useDarEkran();
  // Yerlice S297 / S302: 1279'a kadar bilgi grupları tablonun ÜSTÜNDE ve kapalı (yanda dururken 1024'te
  // tablo yatay kayıyor, ürün adı kırpılıyordu; ölçüldü); ürün satırları 1023'e kadar kart.
  const ust = useDarEkran(1279) || telefon;
  const kartKipi = useDarEkran(1023) || telefon;
  const [alanlarAcik, setAlanlarAcik] = useState(false);
  const gruplarId = useId();

  // Toplam satırı YOK: satır tutarlarının toplamı aşağıdaki "Tutar"dır (aynı sayı iki kez yazılmaz).
  const kolonlar = useMemo<RaporKolonu<BelgeKalemi>[]>(() => [
    { id: 'urun', baslik: 'Ürün', tur: 'metin', deger: (k) => k.urun, esnek: true, kart: 'baslik' },
    { id: 'stok_kodu', baslik: 'Stok Kodu', tur: 'kod', deger: (k) => k.stokKodu, kart: 'alt' },
    { id: 'miktar', baslik: 'Miktar', tur: 'sayi', deger: (k) => k.miktar, kart: 'alt', kartEtiketi: 'Miktar' },
    { id: 'birim', baslik: 'Birim', tur: 'metin', deger: (k) => k.birim, kart: 'alt' },
    { id: 'fiyat', baslik: 'Fiyat', tur: 'para', deger: (k) => k.fiyat, kart: 'olcuAlt' },
    { id: 'iskonto', baslik: 'İskonto', tur: 'metin', hizala: 'sag', deger: (k) => k.iskonto, kart: 'alt', kartEtiketi: 'İskonto' },
    { id: 'tutar', baslik: 'Tutar', tur: 'para', deger: (k) => k.tutar, kart: 'olcu' },
  ], []);

  const ozet = [tarihMetni(belge.tarih), belge.cari].filter(Boolean).join(' · ');
  // Telefonda Genel Toplam altta sabit şeritte; blokta ikinci kez yazılmaz (satırsız fişte blok hiç kalmaz).
  const toplamlar = telefon ? belge.toplamlar.filter((t) => !t.vurgu) : belge.toplamlar;
  return (
    <div className={ust ? 'flex flex-col gap-3 p-4' : 'grid grid-cols-[260px_minmax(0,1fr)] gap-4 p-4 items-start'}>
      <aside className="flex flex-col gap-2.5" data-testid="belge-detay-alanlar">
        {ust && (
          <button
            type="button"
            className="focus-ring flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2.5 text-left min-h-[44px]"
            aria-expanded={alanlarAcik}
            aria-controls={gruplarId}
            onClick={() => setAlanlarAcik((a) => !a)}
            data-testid="belge-detay-alanlar-dugme"
          >
            <span className="text-[13px] font-semibold text-ink-900 shrink-0">Belge bilgileri</span>
            <span className="text-[13px] text-ink-500 truncate min-w-0">{ozet}</span>
            <span className={`ml-auto text-ink-500 transition-transform ${alanlarAcik ? 'rotate-180' : ''}`} aria-hidden="true">▾</span>
          </button>
        )}
        {/* `hidden` özniteliği YETMEZ: `flex` sınıfı onu ezer (390'da ölçüldü, gruplar kapalıyken açık duruyordu). */}
        <div id={gruplarId} className={`${ust && !alanlarAcik ? 'hidden' : 'flex'} flex-col gap-2.5`}>
          <Grup baslik="Tarihler" alanlar={[['Tarih', tarihMetni(belge.tarih)], ['Vade', tarihMetni(belge.vade)]]} />
          <Grup baslik="Cari" alanlar={[['Cari Adı', belge.cari, true], ['Cari Kodu', belge.cariKodu]]} />
          <Grup baslik="Belge" alanlar={[
            ['Belge Türü', belge.tur],
            ['Belge No', belge.belgeNo],
            ['Lokasyon', belge.lokasyon],
            ['Açıklama', belge.aciklama || hareket.aciklama, true],
          ]} />
        </div>
      </aside>

      <div className="flex flex-col gap-3 min-w-0">
        {belge.kalemler.length === 0 ? (
          <p className="m-0 bg-surface border border-border rounded-lg p-4 text-[13px] text-ink-500" data-testid="belge-detay-satir-yok">
            Bu belgede ürün satırı yok{belge.tur ? ` (${belge.tur})` : ''}.
          </p>
        ) : (
          // Blok sarmalayıcı ŞART (Hamam adisyonu): tablo kabı `flex: 1 1 0`, doğrudan flex sütunda 0'a iner.
          <div>
            <RaporTablosu
              testid="belge-kalem-tablosu"
              etiket="Belgenin ürün satırları"
              // Masaüstünde tablo belli boya kadar büyür, sonra kendi içinde kayar; toplam bloğu hep görünür.
              className={kartKipi ? '' : 'max-h-[52vh]'}
              kip={kartKipi ? 'kart' : 'tablo'}
              kartStili="cizgi"
              kolonlar={kolonlar}
              satirlar={belge.kalemler}
              satirKimligi={(k) => k.kimlik}
              bosMesaj="Bu belgede ürün satırı yok"
            />
          </div>
        )}
        {toplamlar.length > 0 && (
        <table className="self-end w-full max-w-[340px] border-collapse bg-surface border border-border rounded-lg text-[13px]" data-testid="belge-toplam-blok">
          <tbody>
            {toplamlar.map((t) => (
                <tr key={t.etiket} className={t.vurgu ? 'border-t border-border' : undefined}>
                  <th scope="row" className={`text-left font-normal px-3 py-1.5 ${t.vurgu ? 'font-semibold text-ink-900' : 'text-ink-500'}`}>{t.etiket}</th>
                  <td className={`mono text-right px-3 py-1.5 ${t.vurgu ? 'font-semibold text-ink-900' : 'text-ink-900'}`}>{paraMetni(t.deger)}</td>
                </tr>
              ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
